import React, { useState, useEffect } from 'react';
import './ExportProgressTracker.css';

/**
 * Floating progress tracker for background exports
 * Shows in bottom-right corner, allows users to navigate while export runs
 */
const ExportProgressTracker = ({
  exports = [],
  onDownload,
  onDismiss,
  onToggleExpanded
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  // Auto-expand when a new export starts
  useEffect(() => {
    const hasActiveExport = exports.some(exp => exp.status === 'processing');
    if (hasActiveExport && isMinimized) {
      setIsMinimized(false);
    }
  }, [exports, isMinimized]);

  if (exports.length === 0) {
    return null;
  }

  // Get the most recent export
  const currentExport = exports[0];
  const activeCount = exports.filter(exp => exp.status === 'processing').length;
  const completedCount = exports.filter(exp => exp.status === 'completed').length;

  const handleToggle = () => {
    if (isMinimized) {
      setIsMinimized(false);
    } else {
      setIsExpanded(!isExpanded);
    }
  };

  const handleMinimize = (e) => {
    e.stopPropagation();
    setIsMinimized(true);
    setIsExpanded(false);
  };

  const handleDownload = (exportId) => {
    onDownload(exportId);
  };

  const handleDismiss = (exportId, e) => {
    e.stopPropagation();
    onDismiss(exportId);
  };

  if (isMinimized) {
    return (
      <div className="export-progress-minimized" onClick={handleToggle}>
        <div className="minimized-content">
          {activeCount > 0 && (
            <>
              <div className="minimized-spinner"></div>
              <span className="minimized-text">
                {activeCount} export{activeCount > 1 ? 's' : ''} in progress
              </span>
            </>
          )}
          {activeCount === 0 && completedCount > 0 && (
            <>
              <svg className="minimized-check" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="7" fill="#28a745"/>
                <path d="M5 8l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span className="minimized-text">
                {completedCount} export{completedCount > 1 ? 's' : ''} ready
              </span>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`export-progress-tracker ${isExpanded ? 'expanded' : ''}`}>
      <div className="export-progress-header" onClick={handleToggle}>
        <div className="export-progress-title">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M6 10L9 13L14 8M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Export Progress</span>
        </div>
        <div className="export-progress-controls">
          <button
            className="export-progress-btn minimize-btn"
            onClick={handleMinimize}
            title="Minimize"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 8H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
          <button
            className="export-progress-btn expand-btn"
            onClick={handleToggle}
            title={isExpanded ? "Collapse" : "Expand"}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
              <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="export-progress-list">
          {exports.map((exportItem) => (
            <div key={exportItem.id} className={`export-item export-item-${exportItem.status}`}>
              <div className="export-item-header">
                <div className="export-item-info">
                  <div className="export-item-name">{exportItem.name}</div>
                  <div className="export-item-meta">
                    {exportItem.pageCount} pages • {exportItem.format.toUpperCase()}
                  </div>
                </div>
                {exportItem.status === 'completed' && (
                  <button
                    className="export-item-dismiss"
                    onClick={(e) => handleDismiss(exportItem.id, e)}
                    title="Dismiss"
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M1 1L13 13M1 13L13 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>
                )}
              </div>

              {exportItem.status === 'processing' && (
                <div className="export-item-progress">
                  <div className="export-progress-bar">
                    <div
                      className="export-progress-fill"
                      style={{ width: `${exportItem.progress || 0}%` }}
                    ></div>
                  </div>
                  <div className="export-item-status">
                    {exportItem.stage || 'Processing...'}
                  </div>
                </div>
              )}

              {exportItem.status === 'completed' && (
                <div className="export-item-actions">
                  <button
                    className="export-download-btn"
                    onClick={() => handleDownload(exportItem.id)}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M8 1V11M8 11L11 8M8 11L5 8M2 11V14H14V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Download PDF
                  </button>
                  <div className="export-item-size">
                    {exportItem.size ? `${(exportItem.size / 1024 / 1024).toFixed(2)} MB` : ''}
                  </div>
                </div>
              )}

              {exportItem.status === 'error' && (
                <div className="export-item-error">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="8" r="7" stroke="#dc3545" strokeWidth="2"/>
                    <path d="M8 4V8M8 10V11" stroke="#dc3545" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <span>{exportItem.error || 'Export failed'}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {!isExpanded && currentExport && (
        <div className="export-progress-summary">
          {currentExport.status === 'processing' && (
            <>
              <div className="export-progress-spinner"></div>
              <div className="export-summary-info">
                <div className="export-summary-name">{currentExport.name}</div>
                <div className="export-summary-stage">
                  {currentExport.stage || 'Processing...'}
                </div>
              </div>
              <div className="export-summary-percent">
                {currentExport.progress || 0}%
              </div>
            </>
          )}
          {currentExport.status === 'completed' && (
            <>
              <svg className="export-check-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" fill="#28a745"/>
                <path d="M7 12l3 3 7-7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <div className="export-summary-info">
                <div className="export-summary-name">{currentExport.name}</div>
                <div className="export-summary-stage">Ready to download</div>
              </div>
              <button
                className="export-download-btn-compact"
                onClick={() => handleDownload(currentExport.id)}
              >
                Download
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ExportProgressTracker;
